import { Box } from "@chakra-ui/react";
import { FundFamilySetupTabProps } from "features/fund-family/setup/FundFamilySetupTabProps";
import {
  useNoticeDocumentTemplateRetrieveSuspense,
  useUpsertNoticeDocumentTemplate,
} from "gen/endpoints/notice-document-template/notice-document-template";
import { NoticeCustomTemplateEnum } from "gen/model/noticeCustomTemplateEnum";
import { NoticeDocumentTemplateRequestAPIDataRequest } from "gen/model/noticeDocumentTemplateRequestAPIDataRequest";
import { NoticeEventTypeEnum } from "gen/model/noticeEventTypeEnum";
import { NoticeGenerationTypeEnum } from "gen/model/noticeGenerationTypeEnum";
import { NoticeTemplateTypeEnum } from "gen/model/noticeTemplateTypeEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { Control, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import { FormFieldPrefix } from "shared/components/form/FormFieldPrefix";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { rootRoute } from "shared/navigation/Routes";
import { enumKeys } from "shared/utils/EnumUtils";
import { useHasPermission } from "shared/utils/PermissionUtils";

interface FundFamilySetupNoticeDocumentTemplateFormProps {
  eventType: NoticeEventTypeEnum;
}

interface FundFamilySetupNoticeDocumentBodyProps {
  formControl: Control<NoticeDocumentTemplateRequestAPIDataRequest>;
  prefix: "cover_letter" | "notice_detail";
}

const FundFamilySetupNoticeDocumentBody: React.FC<
  FundFamilySetupNoticeDocumentBodyProps
> = (props) => {
  const { prefix, formControl } = props;
  const { t } = useTranslation(["notices", "enums"]);
  const generationType = useWatch({
    control: formControl,
    name: `${prefix}.generation_type`,
  });

  return (
    <FormFieldPrefix key={prefix} prefix={prefix}>
      <FormSelect name="generation_type" title="Notice Generation Type">
        {enumKeys(NoticeGenerationTypeEnum).map((generationTypeEnum) => (
          <SelectOption
            key={`generation_type_${generationTypeEnum}`}
            value={generationTypeEnum}
          >
            {t(`enums:NoticeGenerationTypeEnum.${generationTypeEnum}`)}
          </SelectOption>
        ))}
      </FormSelect>
      {generationType == NoticeGenerationTypeEnum.pdf_fill && (
        <FormTextInput
          name="template_id"
          title={t("notices:externalTemplateId")}
        />
      )}
      {generationType == NoticeGenerationTypeEnum.pdf_markdown && (
        <Box marginBottom={10} data-color-mode="light">
          <Box marginBottom={10} data-color-mode="light">
            <FormTextArea
              name="markdown_text"
              title={t("notices:markdownText")}
            />
          </Box>
        </Box>
      )}
      {generationType == NoticeGenerationTypeEnum.pdf_custom_html && (
        <FormSelect name="custom_template" title="Custom Template">
          {enumKeys(NoticeCustomTemplateEnum).map((template) => (
            <SelectOption key={`custom_template_${template}`} value={template}>
              {t(`enums:NoticeCustomTemplateTypeEnum.${template}`)}
            </SelectOption>
          ))}
        </FormSelect>
      )}
    </FormFieldPrefix>
  );
};

const FundFamilySetupNoticeDocumentTemplateForm: React.FC<
  FundFamilySetupNoticeDocumentTemplateFormProps
> = (props) => {
  const { eventType } = props;
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "notices"]);
  const hasConfigPermission = useHasPermission(PermissionTypeEnum.config);
  const { fundFamilyId } = useOutletContext<FundFamilySetupTabProps>();

  const { data: noticeTemplateData } =
    useNoticeDocumentTemplateRetrieveSuspense(fundFamilyId, {
      event_type: eventType,
    });

  const { mutateAsync: upsertNoticeDocumentTemplate } =
    useUpsertNoticeDocumentTemplate();

  const form = useForm<NoticeDocumentTemplateRequestAPIDataRequest>({
    defaultValues: {
      cover_letter: {
        fund_family_id: fundFamilyId,
        event_type: eventType,
        template_type: NoticeTemplateTypeEnum.cover_letter,
        ...(noticeTemplateData.cover_letter ?? {}),
      },
      notice_detail: {
        fund_family_id: fundFamilyId,
        event_type: eventType,
        template_type: NoticeTemplateTypeEnum.notice_detail,
        ...(noticeTemplateData.notice_detail ?? {}),
      },
    },
  });

  const onSubmit = async (
    data: NoticeDocumentTemplateRequestAPIDataRequest,
  ) => {
    await upsertNoticeDocumentTemplate({
      fundFamilyId,
      data: {
        ...(data.cover_letter?.generation_type && {
          cover_letter: data.cover_letter,
        }),
        notice_detail: data.notice_detail,
      },
    });
  };

  return (
    <Form
      form={form}
      isDisabled={!hasConfigPermission}
      onSubmit={onSubmit}
      successMessage={t("notices:upsert.success")}
    >
      <ContentBlockGroup defaultIndex={[0, 1]}>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("notices:templateType.coverLetter")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody variant="form">
            <FundFamilySetupNoticeDocumentBody
              formControl={form.control}
              prefix="cover_letter"
            />
          </ContentBlockBody>
        </ContentBlock>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("notices:templateType.noticeDetail")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody variant="form">
            <FundFamilySetupNoticeDocumentBody
              formControl={form.control}
              prefix="notice_detail"
            />
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      <FormFooter>
        <FormFooterActions>
          <FormCancelButton
            onClick={() => {
              navigate(
                rootRoute({}).fundFamilies({}).fundFamily({ fundFamilyId }).$,
              );
            }}
          />
          <FormSubmitButton>{t("common:submit")}</FormSubmitButton>
        </FormFooterActions>
      </FormFooter>
    </Form>
  );
};

export default FundFamilySetupNoticeDocumentTemplateForm;
